/* eslint-disable react-hooks/exhaustive-deps */
// src/business-types/saloon/modules/orders/SaloonOrderDisplay.tsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Divider, Paper, Typography, styled } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { useOrderDetails } from "./SaloonOrderDisplay/hooks/useOrderDetails";
import OrderHeader from "./SaloonOrderDisplay/components/OrderHeader";
import OrderSummary from "./SaloonOrderDisplay/components/OrderSummary";
import OrderItemsTable from "./SaloonOrderDisplay/components/OrderItemsTable";
import PaymentDialog from "./SaloonOrderDisplay/components/PaymentDialog";
import CancelOrderDialog from "./SaloonOrderDisplay/components/CancelDialog";
import { useCancelHandler } from "./SaloonOrderDisplay/hooks/useCancelHandler";
import { usePaymentHandler } from "./SaloonOrderDisplay/hooks/usePaymentHandler";
import OrderActions from "./SaloonOrderDisplay/components/OrderActions";
import CommonButtons from "src/shared/components/CommonButtons";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import ThermalPrinterTest, { PrintData } from "src/printConfigration/PrintConfigration";
import { PageTitle } from "src/shared/PageTitle";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
  background: "linear-gradient(to bottom, #ffffff, #f9f9f9)",
}));

const SaloonOrderDisplay: React.FC = () => {
  const navigate = useNavigate();
  const { orderid } = useParams();
  const { order, isLoading, outstandingAmount, fetchOrderDetails, updateOrderStatus } = useOrderDetails(orderid);
  const [printOpen, setPrintOpen] = useState<boolean>(false);

  const { paymentData, openPaymentDialog, setOpenPaymentDialog, isProcessing, handlePaymentInputChange, processPayment, resetPaymentData } = usePaymentHandler(
    orderid,
    order,
    outstandingAmount,
    fetchOrderDetails,
  );

  const { cancelData, openCancelDialog, setOpenCancelDialog, handleCancelOrder, setCancelData } = useCancelHandler(updateOrderStatus);

  const subTotals = Number(order?.totalAmount ? Number(order.Finalamount) : Number(order?.Totalamount));
  const printData: PrintData = {
    CustomerName: order?.customer.Name || "",
    CustomerPhon: order?.customer.phone_number || "",
    OrderId: order?.Orderid ?? "",
    OrderStatus: order?.OrderStatus || "",
    discount: Number(order?.discount),
    Date: order?.Orderdate || "",
    Time: order?.Ordertime || "",
    Products:
      order?.Products?.map((p: any) => ({
        productName: p.name || "",
        arabicword: p.arabicword || "",
        amount: Number(p.amount) || 0,
        quantity: Number(p.quantity) || 0,
        totalPrice: Number(p.totalPrice) || 0,
      })) || [],
    Paid: subTotals - Number(order?.RemainingAmount),
    unpaid: Number(order?.RemainingAmount),
    OSB: Number(outstandingAmount),
    paymentMethod: order?.payments?.map((p: any) => p.Paymentmethod).join(", ") || "",
    total: Number(order?.totalAmount),
    subtotal: Number(order?.Finalamount) || 0,
    balanceReturned: Number(order?.balanceReturned) || 0,
  };

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    document.title = "Order › Details › iMetanic";
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Loading order details...</Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Order not found</Typography>
      </Box>
    );
  }

  const remainingAmount = Number(order?.RemainingAmount) || 0;

  return (
    <div className="saloon-order-display-container bg-blue-50">
      <PageTitle title="- Order › Details " />
      <div style={{ display: "flex" }}>
        <div className="order-details-main overflow-auto" style={{ padding: "5px" }}>
          <div className="no-print">
            <CommonButtons className="mb-2" label="BACK" onClick={handleGoBack} variant="secondary" icon={KeyboardReturnIcon} />
          </div>
          <StyledPaper>
            <OrderHeader order={order} outstandingAmount={outstandingAmount} onPrint={() => setPrintOpen(!printOpen)} onGoBack={() => window.history.back()} printData={printData} />
            <Divider sx={{ my: 1 }} />
            {/* Swapped the order of grid items */}
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1.5fr 1fr" }} gap={4}>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Order Items
                </Typography>
                <OrderItemsTable order={order} />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Order Summary
                </Typography>
                <OrderSummary order={order} />
              </Box>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            {remainingAmount === 0 ? (
              <Box display="flex" justifyContent="flex-end" className="mt-4">
                <p className="text-red-500">You have no remaining amount in this order</p>
              </Box>
            ) : (
              <OrderActions order={order} onOpenPayment={() => setOpenPaymentDialog(true)} onOpenCancel={() => setOpenCancelDialog(true)} />
            )}
          </StyledPaper>
        </div>
      </div>
      <PaymentDialog
        open={openPaymentDialog}
        onClose={resetPaymentData}
        order={order}
        paymentData={paymentData}
        onPaymentDataChange={handlePaymentInputChange}
        onProcessPayment={processPayment}
        isProcessing={isProcessing}
      />

      <CancelOrderDialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        order={order}
        outstandingAmount={outstandingAmount}
        cancelData={cancelData}
        onCancelDataChange={setCancelData}
        onConfirmCancel={handleCancelOrder}
      />

      <ToastContainer />
    </div>
  );
};

export default SaloonOrderDisplay;
